import { useEffect, useCallback, useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useSearchStore } from "@/stores";
import { MegaphoneIcon, XIcon, UserIcon } from "lucide-react";
import { SearchResult } from "@/stores/search/searchStore";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { BadgeStatus } from "@/components/dashboard_campaigns/status-badges";
import { StatusValue } from "@/types/types";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function GlobalSearch() {
  const navigate = useNavigate();
  const { getToken, orgId } = useAuth();
  const { isOpen, setIsOpen, results, isLoading, performSearch } =
    useSearchStore();

  const [localQuery, setLocalQuery] = useState("");
  const debouncedQuery = useDebounce(localQuery, 300);

  // Perform search when debounced query changes
  useEffect(() => {
    if (debouncedQuery && isOpen) {
      const searchAsync = async () => {
        const token = await getToken();
        if (token && orgId) {
          performSearch(debouncedQuery, orgId, token);
        }
      };
      searchAsync();
    }
  }, [debouncedQuery, isOpen, performSearch, getToken, orgId]);

  // Keyboard shortcut handler
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsOpen(!isOpen);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [isOpen, setIsOpen]);

  const handleSelect = useCallback(
    (result: SearchResult) => {
      setIsOpen(false);
      navigate(result.url);
      setLocalQuery("");
    },
    [setIsOpen, navigate],
  );

  return (
    <CommandDialog
      open={isOpen}
      onOpenChange={setIsOpen}
      shouldFilter={false}
      showCloseButton={false}
      className="max-w-lg top-[15%] translate-y-0 data-[state=closed]:-translate-y-full data-[state=closed]:fade-out rounded-xl"
    >
      <div className="relative border-b-primary border-b shadow-2xl shadow-primary/20">
        <CommandInput
          placeholder="Search All campaigns and creators..."
          value={localQuery}
          onValueChange={setLocalQuery}
          className="pr-8"
        />
        <Button
          variant="muted-ghost" size="icon-sm"
          className="absolute top-2.5 right-2.5"
          onClick={() => {
            setIsOpen(false);
            setLocalQuery("");
          }}
        >
          <XIcon className="size-4" />
        </Button>
      </div>
      <CommandList className="max-h-[700px]">
        <ScrollArea className="max-h-[400px] overflow-auto [scrollbar-color:--alpha(var(--foreground)/20%)_transparent]">
          {results && results.length === 0 && localQuery && !isLoading && (
            <CommandEmpty className="flex items-center justify-center h-16 text-sm text-muted-foreground">
              <span className="mr-2">
                <XIcon className="size-4" />
              </span>
              No results found.
            </CommandEmpty>
          )}
          {isLoading && (
            <CommandEmpty className="flex items-center justify-center h-16">
              <Spinner className="size-4 text-muted-foreground" />
            </CommandEmpty>
          )}

          {!isLoading && results.length !== 0 && (
            <>
              {results.filter((r) => r.category === "campaigns").length > 0 && (
                <CommandGroup heading="Campaigns" className="pb-2">
                  {results
                    .filter((r) => r.category === "campaigns")
                    .map((result: SearchResult) => (
                      <CommandItem
                        key={result.id}
                        value={result.id}
                        onSelect={() => handleSelect(result)}
                        className="cursor-pointer rounded-sm text-sm"
                      >
                        <MegaphoneIcon className="mx-1 size-4!" />
                        <div className="flex flex-row flex-1 overflow-hidden items-center justify-between">
                          <span className="truncate">{result.title}</span>
                          <span className="text-xs text-muted-foreground">
                            {result.status}
                          </span>
                        </div>
                      </CommandItem>
                    ))}
                </CommandGroup>
              )}

              {results.filter((r) => r.category === "creators").length > 0 && (
                <CommandGroup heading="Creators" className="pb-2">
                  {results
                    .filter((r) => r.category === "creators")
                    .map((result: SearchResult) => (
                      <CommandItem
                        key={result.id}
                        value={result.id}
                        onSelect={() => handleSelect(result)}
                        className="cursor-pointer rounded-sm h-12 pr-8"
                      >
                        {result.avatar ? (
                          <Avatar className="size-8.5 ring-2 ring-muted rounded-pill">
                            <AvatarImage src={result.avatar} alt={result.title} />
                            <AvatarFallback className="text-sm bg-muted-foreground/10">
                              {result.title?.charAt(0)?.toUpperCase() || "?"}
                            </AvatarFallback>
                          </Avatar>
                        ) : (
                          <UserIcon className="mr-2 h-4 w-4" />
                        )}
                        <div className="flex flex-col flex-1 overflow-hidden">
                          <span className="truncate flex flex-row items-center justify-between">
                            @{result.title}
                          </span>
                          {result.subtitle && (
                            <span className="text-xs text-muted-foreground truncate">
                              {result.subtitle}
                            </span>
                          )}
                        </div>
                        <BadgeStatus
                          status={result?.status as StatusValue}
                          size="sm"
                          showIcon={false}
                          showDescription={false}
                        />
                      </CommandItem>
                    ))}
                </CommandGroup>
              )}
            </>
          )}
        </ScrollArea>
      </CommandList>
    </CommandDialog>
  );
}
